import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Checkbox,
  FormControlLabel,
  IconButton,
  Stack,
  Tabs,
  Tab,
  Chip,
  InputAdornment
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as BackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import AcrImageVersionPicker from '../../components/AcrImageVersionPicker';
import ContactFormDialog from '../../components/ContactFormDialog';
import {
  TENANT_ENVIRONMENTS,
  ENV_SHORT_NAME_MAX,
  getEnvironmentShortName,
  toEnvironmentShortName,
  buildTenantServiceUrls,
  buildTenantServiceUrlsForProducts,
  buildTenantNamingPreview,
  buildDeploymentNamingSummary,
  URL_BASE_DOMAIN
} from '../../constants/environments';

const CREATE_CONTACT_VALUE = '__create_new_contact__';
const PILVI_ADMIN_EMAIL = 'admin@pilvisystems.com';
const PILVI_ADMIN_NAME = 'Administrator Pilvisystems';

/** Client-side strong password (matches backend style; shown before create). */
const generateClientAdminPassword = (length = 32) => {
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const lower = 'abcdefghijkmnopqrstuvwxyz';
  const digits = '23456789';
  const symbols = '!@#$%^&*-_=+?';
  const all = upper + lower + digits + symbols;
  const bytes = new Uint8Array(Math.max(16, length));
  crypto.getRandomValues(bytes);
  const required = [
    upper[bytes[0] % upper.length],
    lower[bytes[1] % lower.length],
    digits[bytes[2] % digits.length],
    symbols[bytes[3] % symbols.length]
  ];
  const rest = [];
  for (let i = 4; i < bytes.length; i += 1) {
    rest.push(all[bytes[i] % all.length]);
  }
  const chars = [...required, ...rest];
  for (let i = chars.length - 1; i > 0; i -= 1) {
    const j = bytes[i % bytes.length] % (i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join('');
};

const makePilviAdmin = () => ({
  email: PILVI_ADMIN_EMAIL,
  name: PILVI_ADMIN_NAME,
  role: 'pilvi',
  password: generateClientAdminPassword()
});

const extractEmailDomain = (email) => {
  const m = String(email || '').trim().toLowerCase().match(/@([^@\s]+)$/);
  return m ? m[1] : '';
};

const extractWebsiteDomain = (website) => {
  const raw = String(website || '').trim();
  if (!raw) return '';
  try {
    const url = new URL(raw.includes('://') ? raw : `https://${raw}`);
    return String(url.hostname || '').replace(/^www\./i, '').toLowerCase();
  } catch {
    return raw.replace(/^www\./i, '').split('/')[0].toLowerCase();
  }
};

const sectionBoxSx = (bg, border) => ({
  p: 2,
  height: '100%',
  borderRadius: 2.5,
  border: `1px solid ${border}`,
  background: bg,
  boxShadow: `0 10px 28px -18px ${border}`
});

const SECTION_THEMES = {
  sky: { bg: 'linear-gradient(145deg, #f0f9ff 0%, #e0f2fe 55%, #bae6fd 100%)', border: '#0284c7', title: '#075985', muted: '#0369a1' },
  orange: { bg: 'linear-gradient(145deg, #fff7ed 0%, #ffedd5 55%, #fed7aa 100%)', border: '#ea580c', title: '#9a3412', muted: '#c2410c' },
  magenta: { bg: 'linear-gradient(145deg, #fdf4ff 0%, #fae8ff 55%, #f5d0fe 100%)', border: '#c026d3', title: '#86198f', muted: '#a21caf' },
  cyan: { bg: 'linear-gradient(145deg, #ecfeff 0%, #cffafe 55%, #a5f3fc 100%)', border: '#0891b2', title: '#155e75', muted: '#0e7490' },
  blue: { bg: 'linear-gradient(145deg, #eff6ff 0%, #dbeafe 55%, #bfdbfe 100%)', border: '#2563eb', title: '#1e40af', muted: '#1d4ed8' },
  green: { bg: 'linear-gradient(145deg, #ecfdf5 0%, #d1fae5 55%, #a7f3d0 100%)', border: '#059669', title: '#065f46', muted: '#047857' },
  violet: { bg: 'linear-gradient(145deg, #f5f3ff 0%, #ede9fe 55%, #ddd6fe 100%)', border: '#7c3aed', title: '#5b21b6', muted: '#6d28d9' },
  teal: { bg: 'linear-gradient(145deg, #f0fdfa 0%, #ccfbf1 55%, #99f6e4 100%)', border: '#0d9488', title: '#115e59', muted: '#0f766e' },
  slate: { bg: 'linear-gradient(145deg, #f8fafc 0%, #f1f5f9 55%, #e2e8f0 100%)', border: '#64748b', title: '#334155', muted: '#475569' }
};

/** Numbered section card for 2-column Add Tenant layout. */
const SectionCard = ({ number, title, theme = 'slate', children, noInput = false, fullWidth = false }) => {
  const c = SECTION_THEMES[theme] || SECTION_THEMES.slate;
  return (
    <Grid item xs={12} md={fullWidth ? 12 : 6}>
      <Box sx={sectionBoxSx(c.bg, c.border)}>
        <Typography variant="h6" sx={{ mb: 1, color: c.title, fontWeight: 800 }}>
          ({number}) {title}
        </Typography>
        {noInput ? (
          <>
            <Typography variant="body2" sx={{ color: c.muted, fontWeight: 700, mb: 1 }}>
              No INPUT needed
            </Typography>
            {children}
          </>
        ) : children}
      </Box>
    </Grid>
  );
};

/** Distinct colored panels per software app on Add Tenant. */
const PRODUCT_PANEL_COLORS = [
  { bg: 'linear-gradient(145deg, #fdf4ff 0%, #fae8ff 55%, #f5d0fe 100%)', border: '#c026d3', title: '#86198f', muted: '#a21caf' },
  { bg: 'linear-gradient(145deg, #eff6ff 0%, #dbeafe 55%, #bfdbfe 100%)', border: '#2563eb', title: '#1e40af', muted: '#1d4ed8' },
  { bg: 'linear-gradient(145deg, #ecfdf5 0%, #d1fae5 55%, #a7f3d0 100%)', border: '#059669', title: '#065f46', muted: '#047857' },
  { bg: 'linear-gradient(145deg, #fff7ed 0%, #ffedd5 55%, #fed7aa 100%)', border: '#ea580c', title: '#9a3412', muted: '#c2410c' },
  { bg: 'linear-gradient(145deg, #fef2f2 0%, #fee2e2 55%, #fecaca 100%)', border: '#dc2626', title: '#991b1b', muted: '#b91c1c' },
  { bg: 'linear-gradient(145deg, #f0fdfa 0%, #ccfbf1 55%, #99f6e4 100%)', border: '#0d9488', title: '#115e59', muted: '#0f766e' },
  { bg: 'linear-gradient(145deg, #fefce8 0%, #fef9c3 55%, #fde68a 100%)', border: '#ca8a04', title: '#854d0e', muted: '#a16207' },
  { bg: 'linear-gradient(145deg, #eef2ff 0%, #e0e7ff 55%, #c7d2fe 100%)', border: '#4f46e5', title: '#3730a3', muted: '#4338ca' }
];

const panelColorForKey = (key) => {
  const s = String(key || 'x');
  let hash = 0;
  for (let i = 0; i < s.length; i += 1) hash = ((hash << 5) - hash) + s.charCodeAt(i);
  return PRODUCT_PANEL_COLORS[Math.abs(hash) % PRODUCT_PANEL_COLORS.length];
};

const selectionKey = (type, orgId, itemId) => `${type}:${orgId}:${itemId}`;

/** Prefer branch matching environment shortname (e.g. dev → dev), else default. */
const pickBranchForEnvironment = (branches = [], defaultBranch = 'main', envShort = '') => {
  const list = (branches?.length ? branches : [defaultBranch]).filter(Boolean).map(String);
  const short = String(envShort || '').toLowerCase();
  if (!list.length) return defaultBranch || 'main';
  if (!short) return defaultBranch || list[0];
  const exact = list.find((b) => b.toLowerCase() === short);
  if (exact) return exact;
  const starts = list.find((b) => b.toLowerCase().startsWith(`${short}/`) || b.toLowerCase().startsWith(`${short}-`));
  if (starts) return starts;
  const contains = list.find((b) => b.toLowerCase().includes(short));
  if (contains) return contains;
  return defaultBranch && list.includes(defaultBranch) ? defaultBranch : list[0];
};

const TenantForm = ({
  embedded = false,
  lockAccountId = null,
  lockContactId = null,
  onSuccess = null,
  onCancel = null
} = {}) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isEdit = Boolean(id) && !embedded;

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [accounts, setAccounts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [acrImages, setAcrImages] = useState([]);
  const [k8sClusters, setK8sClusters] = useState([]);
  const [k8sClusterId, setK8sClusterId] = useState('');
  const [editTenantName, setEditTenantName] = useState(false);
  const [tenantNameHint, setTenantNameHint] = useState('');
  const [adminUsers, setAdminUsers] = useState(() => (id ? [] : [makePilviAdmin()]));
  const [adminDraft, setAdminDraft] = useState({
    localPart: '',
    name: '',
    useCustomDomain: false,
    customDomain: ''
  });
  const [revealedAdminPasswords, setRevealedAdminPasswords] = useState({});
  const [pilviAdminRemoved, setPilviAdminRemoved] = useState(false);
  const [applications, setApplications] = useState([]);
  const [services, setServices] = useState([]);
  const [selectedAppIds, setSelectedAppIds] = useState([]);
  const [selectedServiceIds, setSelectedServiceIds] = useState([]);
  const [azureOrgs, setAzureOrgs] = useState([]);
  const [repositorySelections, setRepositorySelections] = useState([]);
  const [serviceUrlTab, setServiceUrlTab] = useState(0);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);

  const [formData, setFormData] = useState({
    tenant_name: '',
    company_name: '',
    account_id: lockAccountId || searchParams.get('account_id') || '',
    contact_id: lockContactId || searchParams.get('contact_id') || '',
    contact_email: '',
    contact_phone: '',
    company_address: '',
    office_direct_number: '',
    website: '',
    plan: 'standard',
    max_users: 50,
    notes: '',
    environment: 'development',
    custom_environment_name: ''
  });

  const suggestTenantName = useCallback(async (companyName, preferredName = '') => {
    if (!companyName && !preferredName) return;
    try {
      const response = await api.get('/tenants/check-name', {
        params: {
          company: companyName || undefined,
          name: preferredName || undefined,
          exclude_id: isEdit ? id : undefined
        }
      });
      const data = response.data.data || {};
      const nextName = data.suggested || data.name || '';
      setFormData((prev) => ({ ...prev, tenant_name: nextName }));
      if (data.available === false && preferredName) {
        setTenantNameHint(`“${preferredName}” is taken. Suggested: ${nextName}`);
      } else {
        setTenantNameHint(nextName ? `Auto-generated unique name: ${nextName}` : '');
      }
    } catch (err) {
      console.error(err);
    }
  }, [id, isEdit]);

  useEffect(() => {
    fetchAccounts();
    if (!isEdit) {
      api.get('/catalog/infrastructure/clusters')
        .then((response) => {
          const list = response.data.data?.clusters || [];
          setK8sClusters(list);
          if (list.length === 1) setK8sClusterId(list[0].id);
        })
        .catch(() => setK8sClusters([]));
      Promise.all([
        api.get('/catalog/applications'),
        api.get('/catalog/services'),
        api.get('/settings/repositories').catch(() => ({ data: { data: {} } }))
      ]).then(([appsRes, svcRes, repoRes]) => {
        setApplications((appsRes.data?.data || []).filter((a) => a.is_active !== false));
        setServices((svcRes.data?.data || []).filter((s) => s.is_active !== false));
        setAzureOrgs(repoRes.data?.data?.azure?.organizations || []);
      }).catch(() => {
        setApplications([]);
        setServices([]);
        setAzureOrgs([]);
      });
    }
    if (isEdit) {
      fetchTenant();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  useEffect(() => {
    if (formData.account_id) {
      fetchContacts(formData.account_id);
    } else {
      setContacts([]);
    }
  }, [formData.account_id]);

  // Prefill company fields when account is locked / preselected (Onboard + query params)
  useEffect(() => {
    if (isEdit || !formData.account_id || !accounts.length) return;
    const selected = accounts.find((a) => a.id === formData.account_id);
    if (!selected) return;
    setFormData((prev) => {
      if (prev.company_name && prev.company_name === (selected.company_name || selected.account_name)) {
        return prev;
      }
      return {
        ...prev,
        company_name: selected.company_name || selected.account_name || prev.company_name,
        company_address: selected.company_address || prev.company_address,
        office_direct_number: selected.office_direct_number || prev.office_direct_number,
        website: selected.website || prev.website
      };
    });
    if (!editTenantName && !formData.tenant_name) {
      suggestTenantName(selected.company_name || selected.account_name || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, formData.account_id, isEdit]);

  // Prefill locked contact from Onboard
  useEffect(() => {
    if (!lockContactId || !contacts.length) return;
    const selectedContact = contacts.find((c) => c.id === lockContactId);
    if (!selectedContact) return;
    setFormData((prev) => ({
      ...prev,
      contact_id: lockContactId,
      contact_email: selectedContact.email || prev.contact_email,
      contact_phone: selectedContact.cell_number || prev.contact_phone
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contacts, lockContactId]);

  // Keep PilviSystems default admin on create unless operator removed it
  useEffect(() => {
    if (isEdit || pilviAdminRemoved) return;
    setAdminUsers((prev) => {
      if (prev.some((u) => u.role === 'pilvi' || u.email === PILVI_ADMIN_EMAIL)) return prev;
      return [makePilviAdmin(), ...prev];
    });
  }, [isEdit, pilviAdminRemoved]);

  // Sync company contact as admin with generated password
  useEffect(() => {
    if (isEdit) return;
    const email = String(formData.contact_email || '').trim().toLowerCase();
    if (!email || !email.includes('@')) return;
    if (email === PILVI_ADMIN_EMAIL) return;

    const contact = contacts.find((c) => c.id === formData.contact_id);
    const name = contact
      ? `${contact.first_name || ''} ${contact.last_name || ''}`.trim()
      : '';

    setAdminUsers((prev) => {
      const existingContact = prev.find((u) => u.role === 'contact');
      if (existingContact?.email === email) {
        if (name && existingContact.name !== name) {
          return prev.map((u) => (u.role === 'contact' ? { ...u, name } : u));
        }
        return prev;
      }
      const withoutContactRole = prev.filter((u) => u.role !== 'contact');
      if (withoutContactRole.some((u) => u.email === email)) {
        return withoutContactRole;
      }
      return [
        ...withoutContactRole,
        {
          email,
          name: name || 'Company contact',
          role: 'contact',
          password: generateClientAdminPassword()
        }
      ];
    });
  }, [isEdit, formData.contact_email, formData.contact_id, contacts]);

  const companyDomain = extractEmailDomain(formData.contact_email)
    || extractWebsiteDomain(formData.website)
    || '';

  // Drop repo/project picks that no longer match any selected catalog product
  useEffect(() => {
    setRepositorySelections((prev) => prev.filter((sel) => {
      const appMatch = (sel.application_ids || []).some((id) => selectedAppIds.includes(id));
      const svcMatch = (sel.service_ids || []).some((id) => selectedServiceIds.includes(id));
      return appMatch || svcMatch;
    }));
  }, [selectedAppIds, selectedServiceIds]);

  const fetchAccounts = async () => {
    setAccountsLoading(true);
    try {
      const response = await api.get('/accounts', {
        params: { page: 1, limit: 500, _ts: Date.now() },
        headers: { 'Cache-Control': 'no-cache' }
      });
      setAccounts(response.data.data || []);
      setError('');
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to load accounts';
      setAccounts([]);
      setError(message);
      toast.error(message);
    } finally {
      setAccountsLoading(false);
    }
  };

  const fetchContacts = async (accountId) => {
    try {
      const response = await api.get('/contacts', {
        params: { account_id: accountId, page: 1, limit: 500, _ts: Date.now() },
        headers: { 'Cache-Control': 'no-cache' }
      });
      const contactsList = response.data.data || [];
      setContacts(contactsList);
      setFormData((prev) => {
        if (prev.contact_id) return prev;
        if (contactsList.length === 0) return prev;
        const preferred = contactsList.find((contact) => contact.is_primary) || contactsList[0];
        return {
          ...prev,
          contact_id: preferred.id,
          contact_email: preferred.email || '',
          contact_phone: preferred.cell_number || ''
        };
      });
    } catch (err) {
      setContacts([]);
      toast.error(err.response?.data?.message || 'Failed to load contacts');
    }
  };

  const fetchTenant = async () => {
    try {
      const response = await api.get(`/tenants/${id}`);
      const tenant = response.data.data;
      setFormData({
        tenant_name: tenant.tenant_name || '',
        company_name: tenant.company_name || '',
        account_id: tenant.account_id || '',
        contact_id: tenant.contact_id || '',
        contact_email: tenant.contact_email || '',
        contact_phone: tenant.contact_phone || '',
        company_address: tenant.company_address || '',
        office_direct_number: tenant.office_direct_number || '',
        website: tenant.website || '',
        plan: tenant.plan || 'standard',
        max_users: tenant.max_users || 50,
        notes: tenant.notes || '',
        environment: tenant.environment || 'development',
        custom_environment_name: tenant.custom_environment_name || ''
      });
      const users = Array.isArray(tenant.admin_users) && tenant.admin_users.length
        ? tenant.admin_users
        : [{ email: tenant.admin_email || '', name: '' }];
      setAdminUsers(users);
      setEditTenantName(true);
      setAcrImages(Array.isArray(tenant.acr_images) ? tenant.acr_images : []);
    } catch (err) {
      setError('Failed to load tenant');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFieldErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
    setFormData((prev) => {
      const next = { ...prev, [name]: value };

      if (name === 'account_id') {
        const selected = accounts.find((account) => account.id === value);
        const companyName = selected?.company_name || selected?.account_name || '';
        next.contact_id = '';
        next.contact_email = '';
        next.contact_phone = '';
        next.company_name = companyName;
        next.company_address = selected?.company_address || '';
        next.office_direct_number = selected?.office_direct_number || '';
        next.website = selected?.website || '';
        if (!isEdit && !editTenantName && companyName) {
          suggestTenantName(companyName);
        }
      }

      if (name === 'contact_id') {
        if (value === CREATE_CONTACT_VALUE) {
          if (!formData.account_id) {
            toast.error('Select a company first');
            return prev;
          }
          setContactDialogOpen(true);
          return prev; // keep previous contact selection while dialog is open
        }
        const selectedContact = contacts.find((c) => c.id === value);
        if (selectedContact) {
          next.contact_email = selectedContact.email || next.contact_email;
          next.contact_phone = selectedContact.cell_number || next.contact_phone;
        }
      }

      if (name === 'environment' && value !== 'custom') {
        next.custom_environment_name = '';
      }

      if (name === 'custom_environment_name') {
        next.custom_environment_name = toEnvironmentShortName(value);
      }

      return next;
    });
  };

  const onTenantNameBlur = async () => {
    if (!formData.tenant_name.trim()) return;
    try {
      const response = await api.get('/tenants/check-name', {
        params: {
          name: formData.tenant_name.trim(),
          exclude_id: isEdit ? id : undefined
        }
      });
      const data = response.data.data || {};
      if (!data.available) {
        setTenantNameHint(`Already in use. Suggested unique name: ${data.suggested}`);
        setFormData((prev) => ({ ...prev, tenant_name: data.suggested }));
      } else {
        setTenantNameHint('Tenant name is available');
      }
    } catch {
      // ignore
    }
  };

  const addAdminUser = () => {
    const localPart = String(adminDraft.localPart || '').trim().toLowerCase().replace(/[^a-z0-9._+-]/g, '');
    if (!localPart) {
      setError('Admin email username is mandatory');
      toast.error('Admin email username is mandatory');
      return;
    }
    const domain = adminDraft.useCustomDomain
      ? String(adminDraft.customDomain || '').trim().toLowerCase().replace(/^@/, '')
      : companyDomain;
    if (!domain) {
      setError(adminDraft.useCustomDomain
        ? 'Custom domain is mandatory'
        : 'Company domain is not available yet — select a contact first, or use a custom domain');
      toast.error(adminDraft.useCustomDomain
        ? 'Custom domain is mandatory'
        : 'Company domain is not available — select a contact or enable custom domain');
      return;
    }
    if (!/^[a-z0-9.-]+\.[a-z]{2,}$/i.test(domain)) {
      setError('Enter a valid email domain');
      toast.error('Enter a valid email domain');
      return;
    }
    const email = `${localPart}@${domain}`;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Enter a valid admin email');
      toast.error('Enter a valid admin email');
      return;
    }
    if (adminUsers.some((u) => u.email.toLowerCase() === email)) {
      setError('That admin email is already in the list');
      toast.error('That admin email is already in the list');
      return;
    }
    setError('');
    setFieldErrors((prev) => ({ ...prev, admin_users: undefined }));
    setAdminUsers((prev) => [...prev, {
      email,
      name: adminDraft.name.trim(),
      role: 'custom',
      password: generateClientAdminPassword()
    }]);
    setAdminDraft({
      localPart: '',
      name: '',
      useCustomDomain: adminDraft.useCustomDomain,
      customDomain: adminDraft.customDomain
    });
  };

  const removeAdminUser = (email) => {
    const target = String(email || '').toLowerCase();
    if (target === PILVI_ADMIN_EMAIL) {
      setPilviAdminRemoved(true);
    }
    setAdminUsers((prev) => prev.filter((u) => u.email.toLowerCase() !== target));
  };

  const restorePilviAdmin = () => {
    setPilviAdminRemoved(false);
    setAdminUsers((prev) => {
      if (prev.some((u) => u.email === PILVI_ADMIN_EMAIL)) return prev;
      return [makePilviAdmin(), ...prev];
    });
  };

  const copyAdminPassword = async (password) => {
    try {
      await navigator.clipboard.writeText(password);
      toast.success('Password copied');
    } catch {
      toast.error('Could not copy password');
    }
  };

  const toggleRepositorySelection = (entry) => {
    const envShort = getEnvironmentShortName(formData.environment, formData.custom_environment_name);
    const key = selectionKey(entry.type, entry.org_id, entry.item_id);
    setRepositorySelections((prev) => {
      const exists = prev.some((s) => selectionKey(s.type, s.org_id, s.item_id) === key);
      if (exists) return prev.filter((s) => selectionKey(s.type, s.org_id, s.item_id) !== key);
      const withBranch = entry.type === 'repository'
        ? {
          ...entry,
          selected_branch: pickBranchForEnvironment(
            entry.branches || [],
            entry.default_branch,
            envShort
          )
        }
        : entry;
      return [...prev, withBranch];
    });
  };

  // When environment changes, re-default selected repo branches to matching env branch (e.g. dev → dev)
  useEffect(() => {
    const envShort = getEnvironmentShortName(formData.environment, formData.custom_environment_name);
    if (!envShort) return;
    setRepositorySelections((prev) => {
      if (!prev.length) return prev;
      let changed = false;
      const next = prev.map((sel) => {
        if (sel.type !== 'repository') return sel;
        let branches = sel.branches || [];
        if (!branches.length) {
          azureOrgs.forEach((org) => {
            const repo = (org.repositories || []).find((r) => String(r.id) === String(sel.item_id));
            if (repo) branches = repo.branches || [];
          });
        }
        const picked = pickBranchForEnvironment(branches, sel.default_branch, envShort);
        if (picked !== sel.selected_branch) {
          changed = true;
          return { ...sel, selected_branch: picked, branches };
        }
        return sel;
      });
      return changed ? next : prev;
    });
  }, [formData.environment, formData.custom_environment_name, azureOrgs]);

  const updateSelectionBranch = (entry, branch) => {
    const key = selectionKey(entry.type, entry.org_id, entry.item_id);
    setRepositorySelections((prev) => prev.map((s) =>
      selectionKey(s.type, s.org_id, s.item_id) === key
        ? { ...s, selected_branch: branch }
        : s
    ));
  };

  const isSelectionChecked = (type, orgId, itemId) =>
    repositorySelections.some((s) => selectionKey(s.type, s.org_id, s.item_id) === selectionKey(type, orgId, itemId));

  const showMandatoryError = (message, fields = {}) => {
    setFieldErrors(fields);
    setError(message);
    toast.error(message);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const validateMandatoryFields = () => {
    const missing = [];
    const fields = {};

    if (!isEdit && !formData.account_id) {
      missing.push('Company / Account');
      fields.account_id = 'Company / Account is mandatory';
    }
    if (!String(formData.company_name || '').trim()) {
      missing.push('Company Name');
      fields.company_name = 'Company Name is mandatory';
    }
    if (!String(formData.tenant_name || '').trim()) {
      missing.push('Tenant Name');
      fields.tenant_name = 'Tenant Name is mandatory';
    }
    if (!isEdit && !formData.contact_id) {
      missing.push('Contact');
      fields.contact_id = 'Contact is mandatory';
    }
    if (!String(formData.contact_email || '').trim()) {
      missing.push('Contact Email');
      fields.contact_email = 'Contact Email is mandatory';
    }
    if (!formData.environment) {
      missing.push('Environment');
      fields.environment = 'Environment is mandatory';
    }
    if (formData.environment === 'custom' && !String(formData.custom_environment_name || '').trim()) {
      missing.push('Custom environment shortname');
      fields.custom_environment_name = 'Custom environment shortname is mandatory';
    }

    const cleanedAdmins = adminUsers
      .map((u) => ({
        email: String(u.email || '').trim().toLowerCase(),
        name: String(u.name || '').trim(),
        role: u.role || 'custom',
        password: u.password || generateClientAdminPassword()
      }))
      .filter((u) => u.email);
    if (cleanedAdmins.length === 0) {
      missing.push('Initial Admin User');
      fields.admin_users = 'At least one Initial Admin User is mandatory';
    }

    if (!isEdit) {
      if (selectedAppIds.length === 0 && selectedServiceIds.length === 0) {
        missing.push('Software Application or Service');
        fields.catalog_products = 'Software Application or Service is mandatory';
      } else if (selectedAppIds.length === 0) {
        missing.push('Software Application');
        fields.catalog_products = 'Software Application is mandatory';
      }
      if (k8sClusters.length > 0 && !k8sClusterId) {
        missing.push('Kubernetes Infrastructure Cluster');
        fields.k8s_cluster_id = 'Kubernetes Infrastructure Cluster is mandatory';
      }
    }

    if (missing.length === 0) {
      return { ok: true, cleanedAdmins, fields: {} };
    }

    const message = missing.length === 1
      ? `${missing[0]} is mandatory.`
      : `The following fields are mandatory: ${missing.join(', ')}.`;
    return { ok: false, message, fields, cleanedAdmins };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setFieldErrors({});

    try {
      const validation = validateMandatoryFields();
      if (!validation.ok) {
        showMandatoryError(validation.message, validation.fields);
        setSaving(false);
        return;
      }

      const environmentShortName = getEnvironmentShortName(
        formData.environment,
        formData.custom_environment_name
      );
      if (!environmentShortName) {
        showMandatoryError('Environment shortname is mandatory.', { environment: 'Environment shortname is mandatory' });
        setSaving(false);
        return;
      }
      if (environmentShortName.length > ENV_SHORT_NAME_MAX) {
        showMandatoryError(
          `Environment shortname must be at most ${ENV_SHORT_NAME_MAX} characters.`,
          { custom_environment_name: `Max ${ENV_SHORT_NAME_MAX} characters` }
        );
        setSaving(false);
        return;
      }

      const cleanedAdmins = validation.cleanedAdmins;

      const catalogProducts = [
        ...applications
          .filter((a) => selectedAppIds.includes(a.id))
          .map((a) => ({
            kind: 'application',
            id: a.id,
            short_name: a.short_name,
            name: a.name
          })),
        ...services
          .filter((s) => selectedServiceIds.includes(s.id))
          .map((s) => ({
            kind: 'service',
            id: s.id,
            short_name: s.short_name,
            name: s.name
          }))
      ];

      const softwareProducts = catalogProducts.filter((p) => p.kind === 'application');
      const primarySoftware = softwareProducts[0] || catalogProducts[0];
      if (!isEdit && !primarySoftware?.short_name) {
        showMandatoryError(
          'Software Application is mandatory (must include a shortname for service URLs).',
          { catalog_products: 'Software Application is mandatory' }
        );
        setSaving(false);
        return;
      }

      const builtUrls = buildTenantServiceUrls({
        tenantName: formData.tenant_name,
        environmentShortName,
        softwareShortName: primarySoftware.short_name,
        services: ['frontend', 'backend']
      });
      const builtFrontend = builtUrls.find((s) => s.role === 'frontend')?.url || null;
      const builtBackend = builtUrls.find((s) => s.role === 'backend')?.url || null;

      const payload = {
        ...formData,
        contact_id: formData.contact_id || null,
        account_id: formData.account_id || null,
        admin_users: cleanedAdmins,
        admin_email: cleanedAdmins[0].email,
        environment_short_name: environmentShortName,
        custom_environment_name: formData.environment === 'custom'
          ? environmentShortName
          : null,
        instance_url: builtFrontend,
        api_url: builtBackend,
        frontend_url: builtFrontend,
        backend_url: builtBackend,
        acr_images: acrImages,
        catalog_products: catalogProducts,
        repository_selections: repositorySelections,
        k8s_cluster_id: k8sClusterId || null
      };

      if (isEdit) {
        await api.put(`/tenants/${id}`, payload);
        toast.success('Tenant updated successfully');
        if (onSuccess) onSuccess();
        else navigate('/tenants');
      } else {
        const response = await api.post('/tenants', payload);
        toast.success(`Tenant created! ID: ${response.data.data.tenant_id}`);
        if (onSuccess) onSuccess(response.data.data);
        else navigate('/tenants');
      }
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to save tenant';
      setError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const environmentShortNamePreview = getEnvironmentShortName(
    formData.environment,
    formData.custom_environment_name
  );
  const catalogProductsPreview = [
    ...applications
      .filter((a) => selectedAppIds.includes(a.id))
      .map((a) => ({ kind: 'application', id: a.id, short_name: a.short_name, name: a.name })),
    ...services
      .filter((s) => selectedServiceIds.includes(s.id))
      .map((s) => ({ kind: 'service', id: s.id, short_name: s.short_name, name: s.name }))
  ];
  const selectedShortNames = catalogProductsPreview.map((p) => p.short_name).filter(Boolean);
  const softwareProductsPreview = catalogProductsPreview.filter((p) => p.kind === 'application');
  const namingRows = buildTenantNamingPreview({
    catalogProducts: catalogProductsPreview,
    tenantName: formData.tenant_name,
    environmentShortName: environmentShortNamePreview,
    roles: ['frontend', 'backend', 'database']
  });
  const primarySoftware = softwareProductsPreview[0] || catalogProductsPreview.find((p) => p.short_name);
  const namingSummary = buildDeploymentNamingSummary({
    catalogShortName: primarySoftware?.short_name || '',
    tenantName: formData.tenant_name,
    environmentShortName: environmentShortNamePreview
  });
  const serviceUrls = buildTenantServiceUrlsForProducts({
    tenantName: formData.tenant_name,
    environmentShortName: environmentShortNamePreview,
    softwareProducts: softwareProductsPreview.length
      ? softwareProductsPreview
      : catalogProductsPreview.filter((p) => p.short_name),
    services: ['frontend', 'backend']
  });

  return (
    <Box>
      {!embedded && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Button startIcon={<BackIcon />} onClick={() => (onCancel ? onCancel() : navigate('/tenants'))}>
            Back
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {isEdit ? 'Edit Tenant' : 'Create New Tenant'}
          </Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2.5 }} elevation={embedded ? 0 : 1}>
        <form onSubmit={handleSubmit} noValidate>
          <Grid container spacing={2} alignItems="stretch">
          <SectionCard number={1} title="Basic Information" theme="sky">
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required error={Boolean(fieldErrors.account_id)}>
                  <InputLabel>Company Name (from Account)</InputLabel>
                  <Select
                    name="account_id"
                    value={formData.account_id}
                    label="Company Name (from Account)"
                    onChange={handleChange}
                    disabled={accountsLoading || Boolean(lockAccountId)}
                    sx={{ bgcolor: 'rgba(255,255,255,0.85)' }}
                  >
                    <MenuItem value="" disabled>Select Account</MenuItem>
                    {accounts.map((account) => (
                      <MenuItem key={account.id} value={account.id}>
                        {(account.company_name || account.account_name)}
                        {account.account_code ? ` (${account.account_code})` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                  {fieldErrors.account_id && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                      {fieldErrors.account_id}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Company Name"
                  name="company_name"
                  value={formData.company_name}
                  InputProps={{ readOnly: true }}
                  error={Boolean(fieldErrors.company_name)}
                  helperText={fieldErrors.company_name || 'Auto-populated from selected Account'}
                  sx={{ bgcolor: 'rgba(255,255,255,0.85)', borderRadius: 1 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Tenant Name"
                  name="tenant_name"
                  value={formData.tenant_name}
                  onChange={handleChange}
                  onBlur={onTenantNameBlur}
                  disabled={!editTenantName}
                  error={Boolean(fieldErrors.tenant_name)}
                  helperText={
                    fieldErrors.tenant_name
                    || tenantNameHint
                    || (editTenantName
                      ? 'Editable — must be unique across all companies'
                      : 'Generated from company name (enable edit to change)')
                  }
                  sx={{ bgcolor: 'rgba(255,255,255,0.85)', borderRadius: 1 }}
                />
                <FormControlLabel
                  sx={{ mt: 0.5 }}
                  control={(
                    <Checkbox
                      checked={editTenantName}
                      onChange={(e) => setEditTenantName(e.target.checked)}
                    />
                  )}
                  label="Edit tenant name"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required disabled={!formData.account_id || Boolean(lockContactId)} error={Boolean(fieldErrors.contact_id)}>
                  <InputLabel>Contact</InputLabel>
                  <Select
                    name="contact_id"
                    value={formData.contact_id}
                    label="Contact"
                    onChange={handleChange}
                    sx={{ bgcolor: 'rgba(255,255,255,0.85)' }}
                  >
                    <MenuItem value="">Select Contact</MenuItem>
                    {!lockContactId && (
                      <MenuItem value={CREATE_CONTACT_VALUE} sx={{ fontWeight: 700, color: 'primary.main' }}>
                        + Create new contact…
                      </MenuItem>
                    )}
                    {contacts.map((contact) => (
                      <MenuItem key={contact.id} value={contact.id}>
                        {contact.first_name} {contact.last_name} ({contact.email})
                      </MenuItem>
                    ))}
                  </Select>
                  {fieldErrors.contact_id ? (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                      {fieldErrors.contact_id}
                    </Typography>
                  ) : (!formData.account_id ? (
                    <Typography variant="caption" color="text.secondary">
                      Select a company first, then choose or create a contact.
                    </Typography>
                  ) : null)}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  type="email"
                  label="Contact Email"
                  name="contact_email"
                  value={formData.contact_email}
                  InputProps={{ readOnly: true }}
                  error={Boolean(fieldErrors.contact_email)}
                  helperText={fieldErrors.contact_email || 'Auto-populated from selected Contact'}
                  sx={{ bgcolor: 'rgba(255,255,255,0.85)', borderRadius: 1 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Company Address"
                  name="company_address"
                  value={formData.company_address}
                  InputProps={{ readOnly: true }}
                  sx={{ bgcolor: 'rgba(255,255,255,0.85)', borderRadius: 1 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Contact Cell Number"
                  name="contact_phone"
                  value={formData.contact_phone}
                  InputProps={{ readOnly: true }}
                  sx={{ bgcolor: 'rgba(255,255,255,0.85)', borderRadius: 1 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Office Direct Number"
                  name="office_direct_number"
                  value={formData.office_direct_number}
                  InputProps={{ readOnly: true }}
                  sx={{ bgcolor: 'rgba(255,255,255,0.85)', borderRadius: 1 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Website"
                  name="website"
                  value={formData.website}
                  InputProps={{ readOnly: true }}
                  sx={{ bgcolor: 'rgba(255,255,255,0.85)', borderRadius: 1 }}
                />
              </Grid>
            </Grid>
          </SectionCard>

          <SectionCard number={2} title="Environment" theme="orange">
            <Typography variant="body2" sx={{ mb: 2, color: SECTION_THEMES.orange.muted }}>
              Choose the target environment. Repo branches below default to this shortname (e.g. dev → <code>dev</code> branch).
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth required error={Boolean(fieldErrors.environment)}>
                  <InputLabel>Environment</InputLabel>
                  <Select
                    name="environment"
                    value={formData.environment}
                    label="Environment"
                    onChange={handleChange}
                    sx={{ bgcolor: 'rgba(255,255,255,0.85)' }}
                  >
                    {TENANT_ENVIRONMENTS.map((env) => (
                      <MenuItem key={env.value} value={env.value}>
                        {env.label}
                        {env.shortName ? ` (${env.shortName})` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                  {fieldErrors.environment && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                      {fieldErrors.environment}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                {formData.environment === 'custom' ? (
                  <TextField
                    fullWidth
                    required
                    label="Custom shortname"
                    name="custom_environment_name"
                    value={formData.custom_environment_name}
                    onChange={handleChange}
                    placeholder="e.g. partnersandbox"
                    error={Boolean(fieldErrors.custom_environment_name)}
                    helperText={
                      fieldErrors.custom_environment_name
                      || `Single word, letters/numbers only, max ${ENV_SHORT_NAME_MAX} characters`
                    }
                    inputProps={{ maxLength: ENV_SHORT_NAME_MAX }}
                    sx={{ bgcolor: 'rgba(255,255,255,0.85)', borderRadius: 1 }}
                  />
                ) : (
                  <TextField
                    fullWidth
                    label="Environment shortname"
                    value={getEnvironmentShortName(formData.environment)}
                    InputProps={{ readOnly: true }}
                    helperText="Auto-set from the selected environment — used as default ADO branch"
                    sx={{ bgcolor: 'rgba(255,255,255,0.85)', borderRadius: 1 }}
                  />
                )}
              </Grid>
            </Grid>
          </SectionCard>

          {!isEdit && (
            <SectionCard number={3} title="Software Applications & Services" theme="magenta">
              <Typography variant="body2" sx={{ mb: 2, color: '#a21caf' }}>
                Select products for this tenant. Container image options are filtered to repositories
                linked to these shortnames in Settings → Container Images (ACR).
              </Typography>
{fieldErrors.catalog_products && (
                <Alert severity="error" sx={{ mb: 2 }}>{fieldErrors.catalog_products}</Alert>
              )}
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required error={Boolean(fieldErrors.catalog_products)}>
                    <InputLabel>Software Applications</InputLabel>
                    <Select
                      multiple
                      label="Software Applications"
                      value={selectedAppIds}
                      onChange={(e) => {
                        setSelectedAppIds(e.target.value);
                        setFieldErrors((prev) => ({ ...prev, catalog_products: undefined }));
                      }}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((id) => {
                            const app = applications.find((a) => a.id === id);
                            return <Chip key={id} size="small" label={app?.short_name || app?.name || id} />;
                          })}
                        </Box>
                      )}
                      sx={{ bgcolor: 'rgba(255,255,255,0.85)' }}
                    >
                      {applications.map((app) => (
                        <MenuItem key={app.id} value={app.id}>
                          <Checkbox checked={selectedAppIds.includes(app.id)} />
                          {app.name}
                          {app.short_name ? ` (${app.short_name})` : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Services / Framework</InputLabel>
                    <Select
                      multiple
                      label="Services / Framework"
                      value={selectedServiceIds}
                      onChange={(e) => setSelectedServiceIds(e.target.value)}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((id) => {
                            const svc = services.find((s) => s.id === id);
                            return <Chip key={id} size="small" label={svc?.short_name || svc?.name || id} />;
                          })}
                        </Box>
                      )}
                      sx={{ bgcolor: 'rgba(255,255,255,0.85)' }}
                    >
                      {services.map((svc) => (
                        <MenuItem key={svc.id} value={svc.id}>
                          <Checkbox checked={selectedServiceIds.includes(svc.id)} />
                          {svc.name}
                          {svc.short_name ? ` (${svc.short_name})` : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

            </SectionCard>
          )}

          {!isEdit && (selectedAppIds.length > 0 || selectedServiceIds.length > 0) && (
            <SectionCard number={4} title="Azure DevOps repositories & projects" theme="slate" fullWidth>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Branches default to the Environment shortname (e.g. dev → <code>dev</code>). Tag repos under Repository Settings.
              </Typography>
              <Grid container spacing={2}>
                {[
                  ...applications.filter((a) => selectedAppIds.includes(a.id)).map((a) => ({
                    kind: 'application',
                    id: a.id,
                    short_name: a.short_name,
                    name: a.name
                  })),
                  ...services.filter((s) => selectedServiceIds.includes(s.id)).map((s) => ({
                    kind: 'service',
                    id: s.id,
                    short_name: s.short_name,
                    name: s.name
                  }))
                ].map((product) => {
                  const colors = panelColorForKey(product.short_name || product.id);
                  const matchesProduct = (item) => {
                    if (product.kind === 'application') {
                      return (item.application_ids || []).includes(product.id);
                    }
                    return (item.service_ids || []).includes(product.id);
                  };
                  const matchedRepos = [];
                  const matchedProjects = [];
                  azureOrgs.forEach((org) => {
                    (org.repositories || []).filter(matchesProduct).forEach((r) => {
                      matchedRepos.push({ org, repo: r });
                    });
                    (org.projects || []).filter(matchesProduct).forEach((p) => {
                      matchedProjects.push({ org, project: p });
                    });
                  });
                  const envShort = getEnvironmentShortName(
                    formData.environment,
                    formData.custom_environment_name
                  );

                  return (
                    <Grid item xs={12} md={6} key={`${product.kind}-${product.id}`}>
                      <Box sx={sectionBoxSx(colors.bg, colors.border)}>
                        <Typography variant="h6" sx={{ mb: 0.5, color: colors.title, fontWeight: 800 }}>
                          {product.name}
                          {product.short_name ? (
                            <Chip
                              size="small"
                              label={product.short_name}
                              sx={{ ml: 1, bgcolor: 'rgba(255,255,255,0.7)', fontWeight: 700 }}
                            />
                          ) : null}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2, color: colors.muted }}>
                          Repositories and projects tagged for this {product.kind === 'application' ? 'application' : 'service'}.
                        </Typography>

                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: colors.title }}>
                          Repositories
                        </Typography>
                        {matchedRepos.length === 0 ? (
                          <Alert severity="info" sx={{ mb: 2 }}>
                            No repositories tagged for {product.short_name || product.name}.
                          </Alert>
                        ) : (
                          <Stack spacing={1} sx={{ mb: 2 }}>
                            {matchedRepos.map(({ org, repo }) => {
                              const checked = isSelectionChecked('repository', org.id, repo.id);
                              const current = repositorySelections.find(
                                (s) => selectionKey(s.type, s.org_id, s.item_id) === selectionKey('repository', org.id, repo.id)
                              );
                              return (
                                <Box
                                  key={`${org.id}-${repo.id}`}
                                  sx={{
                                    p: 1.5,
                                    borderRadius: 1.5,
                                    bgcolor: 'rgba(255,255,255,0.8)',
                                    border: `1px solid ${colors.border}`
                                  }}
                                >
                                  <FormControlLabel
                                    control={(
                                      <Checkbox
                                        checked={checked}
                                        onChange={() => toggleRepositorySelection({
                                          type: 'repository',
                                          organization: org.organization,
                                          org_id: org.id,
                                          item_id: repo.id,
                                          name: repo.name,
                                          project: repo.project,
                                          url: repo.url,
                                          default_branch: repo.default_branch,
                                          branches: repo.branches || [],
                                          selected_branch: pickBranchForEnvironment(
                                            repo.branches || [],
                                            repo.default_branch,
                                            envShort
                                          ),
                                          application_ids: repo.application_ids || [],
                                          service_ids: repo.service_ids || []
                                        })}
                                      />
                                    )}
                                    label={(
                                      <Typography fontWeight={700}>
                                        {repo.name}
                                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                          {org.organization} / {repo.project}
                                        </Typography>
                                      </Typography>
                                    )}
                                  />
                                  {checked && (
                                    <FormControl size="small" sx={{ ml: 4, mt: 0.5, minWidth: 200 }}>
                                      <InputLabel>Branch for tenant</InputLabel>
                                      <Select
                                        label="Branch for tenant"
                                        value={current?.selected_branch || pickBranchForEnvironment(repo.branches || [], repo.default_branch, envShort)}
                                        onChange={(e) => updateSelectionBranch({
                                          type: 'repository',
                                          org_id: org.id,
                                          item_id: repo.id
                                        }, e.target.value)}
                                      >
                                        {(repo.branches?.length ? repo.branches : [repo.default_branch || 'main']).map((b) => (
                                          <MenuItem key={b} value={b}>{b}</MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  )}
                                </Box>
                              );
                            })}
                          </Stack>
                        )}

                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: colors.title }}>
                          Projects
                        </Typography>
                        {matchedProjects.length === 0 ? (
                          <Alert severity="info">
                            No projects tagged for {product.short_name || product.name}.
                          </Alert>
                        ) : (
                          <Stack spacing={1}>
                            {matchedProjects.map(({ org, project }) => (
                              <Box
                                key={`${org.id}-${project.id}`}
                                sx={{
                                  p: 1.25,
                                  borderRadius: 1.5,
                                  bgcolor: 'rgba(255,255,255,0.8)',
                                  border: `1px solid ${colors.border}`
                                }}
                              >
                                <FormControlLabel
                                  control={(
                                    <Checkbox
                                      checked={isSelectionChecked('project', org.id, project.id)}
                                      onChange={() => toggleRepositorySelection({
                                        type: 'project',
                                        organization: org.organization,
                                        org_id: org.id,
                                        item_id: project.id,
                                        name: project.name,
                                        project: project.name,
                                        url: project.url || null,
                                        default_branch: null,
                                        selected_branch: null,
                                        application_ids: project.application_ids || [],
                                        service_ids: project.service_ids || []
                                      })}
                                    />
                                  )}
                                  label={(
                                    <Typography fontWeight={700}>
                                      {project.name}
                                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                        {org.organization}
                                      </Typography>
                                    </Typography>
                                  )}
                                />
                              </Box>
                            ))}
                          </Stack>
                        )}
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </SectionCard>
          )}

          {!isEdit && (
            <SectionCard number={5} title="Container Images (ACR)" theme="cyan">
              <AcrImageVersionPicker
                value={acrImages}
                onChange={setAcrImages}
                shortNames={selectedShortNames}
              />
            </SectionCard>
          )}

          <SectionCard number={6} title="Initial Admin Users" theme="blue">
            <Typography variant="body2" sx={{ mb: 2, color: '#1d4ed8' }}>
              Default: <strong>{PILVI_ADMIN_EMAIL}</strong> (Administrator Pilvisystems) and the company contact.
              Passwords are generated automatically — copy them before create. You may remove the PilviSystems
              admin before submit if needed.
            </Typography>
            {fieldErrors.admin_users && (
              <Alert severity="error" sx={{ mb: 2 }}>{fieldErrors.admin_users}</Alert>
            )}

            {pilviAdminRemoved && !isEdit && (
              <Alert
                severity="warning"
                sx={{ mb: 2 }}
                action={(
                  <Button color="inherit" size="small" onClick={restorePilviAdmin}>
                    Restore
                  </Button>
                )}
              >
                PilviSystems default admin was removed. It is recommended for every tenant.
              </Alert>
            )}

            <Stack spacing={1.25} sx={{ mb: 2 }}>
              {adminUsers.map((user) => (
                <Box
                  key={user.email}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    p: 1.25,
                    borderRadius: 1.5,
                    bgcolor: 'rgba(255,255,255,0.75)',
                    border: '1px solid #93c5fd'
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                      <Typography fontWeight={700} noWrap>{user.email}</Typography>
                      {user.role === 'pilvi' && <Chip size="small" color="primary" label="PilviSystems" />}
                      {user.role === 'contact' && <Chip size="small" color="secondary" label="Company contact" />}
                      {user.role === 'custom' && <Chip size="small" variant="outlined" label="Additional" />}
                    </Box>
                    {user.name ? (
                      <Typography variant="caption" color="text.secondary" display="block">{user.name}</Typography>
                    ) : null}
                    {user.password ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                            bgcolor: 'rgba(37,99,235,0.08)',
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                            wordBreak: 'break-all'
                          }}
                        >
                          {revealedAdminPasswords[user.email]
                            ? user.password
                            : '••••••••••••••••••••••••'}
                        </Typography>
                        <IconButton
                          size="small"
                          aria-label="Toggle password"
                          onClick={() => setRevealedAdminPasswords((prev) => ({
                            ...prev,
                            [user.email]: !prev[user.email]
                          }))}
                        >
                          {revealedAdminPasswords[user.email]
                            ? <VisibilityOffIcon fontSize="small" />
                            : <VisibilityIcon fontSize="small" />}
                        </IconButton>
                        <IconButton
                          size="small"
                          aria-label="Copy password"
                          onClick={() => copyAdminPassword(user.password)}
                        >
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : null}
                  </Box>
                  <IconButton
                    size="small"
                    aria-label={`Remove ${user.email}`}
                    onClick={() => removeAdminUser(user.email)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Stack>

            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: '#1e40af' }}>
              Add additional admin
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Email uses the company domain{companyDomain ? ` (${companyDomain})` : ''} by default. Enable custom domain to override.
            </Typography>
            <FormControlLabel
              sx={{ mb: 1 }}
              control={(
                <Checkbox
                  checked={adminDraft.useCustomDomain}
                  onChange={(e) => setAdminDraft((p) => ({
                    ...p,
                    useCustomDomain: e.target.checked
                  }))}
                />
              )}
              label="Use custom domain"
            />
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={adminDraft.useCustomDomain ? 3 : 4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Username"
                  value={adminDraft.localPart}
                  onChange={(e) => setAdminDraft((p) => ({ ...p, localPart: e.target.value }))}
                  placeholder="jsmith"
                  sx={{ bgcolor: 'rgba(255,255,255,0.8)', borderRadius: 1 }}
                />
              </Grid>
              <Grid item xs={12} md={adminDraft.useCustomDomain ? 4 : 3}>
                {adminDraft.useCustomDomain ? (
                  <TextField
                    fullWidth
                    size="small"
                    label="Custom domain"
                    value={adminDraft.customDomain}
                    onChange={(e) => setAdminDraft((p) => ({ ...p, customDomain: e.target.value }))}
                    placeholder="example.com"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">@</InputAdornment>
                    }}
                    sx={{ bgcolor: 'rgba(255,255,255,0.8)', borderRadius: 1 }}
                  />
                ) : (
                  <TextField
                    fullWidth
                    size="small"
                    label="Company domain"
                    value={companyDomain ? `@${companyDomain}` : 'Select contact first'}
                    InputProps={{ readOnly: true }}
                    sx={{ bgcolor: 'rgba(255,255,255,0.8)', borderRadius: 1 }}
                  />
                )}
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Display name (optional)"
                  value={adminDraft.name}
                  onChange={(e) => setAdminDraft((p) => ({ ...p, name: e.target.value }))}
                  sx={{ bgcolor: 'rgba(255,255,255,0.8)', borderRadius: 1 }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={addAdminUser}
                  sx={{ bgcolor: '#2563eb', '&:hover': { bgcolor: '#1d4ed8' } }}
                >
                  Add
                </Button>
              </Grid>
            </Grid>
            {(adminDraft.localPart || companyDomain || adminDraft.customDomain) && (
              <Typography variant="caption" sx={{ mt: 1, display: 'block', fontFamily: 'monospace' }}>
                Preview:{' '}
                {`${(adminDraft.localPart || 'user').toLowerCase()}@${
                  adminDraft.useCustomDomain
                    ? (adminDraft.customDomain || 'domain.com')
                    : (companyDomain || 'company.com')
                }`}
              </Typography>
            )}
          </SectionCard>

          <SectionCard number={7} title="Plan & Limits" theme="green">
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Plan</InputLabel>
                  <Select
                    name="plan"
                    value={formData.plan}
                    label="Plan"
                    onChange={handleChange}
                    sx={{ bgcolor: 'rgba(255,255,255,0.8)' }}
                  >
                    <MenuItem value="basic">Basic</MenuItem>
                    <MenuItem value="standard">Standard</MenuItem>
                    <MenuItem value="enterprise">Enterprise</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label="Max Users"
                  name="max_users"
                  value={formData.max_users}
                  onChange={handleChange}
                  inputProps={{ min: 1 }}
                  sx={{ bgcolor: 'rgba(255,255,255,0.8)', borderRadius: 1 }}
                />
              </Grid>
            </Grid>
          </SectionCard>

          <SectionCard number={8} title="Service URLs & Kubernetes names" theme="violet" noInput>
            <Typography variant="caption" display="block" sx={{ mb: 1, color: '#6d28d9' }}>
              Preview only — namespace {'{software}-{tenant}'}, pods {'{software}-{tenant}-{service}'},
              DB {'{software}-{tenant}-{env}-db'}, branch {'{tenant}-{env}-xxxxxxxx'}.
            </Typography>
            {namingSummary && (
              <Box
                sx={{
                  mb: 2,
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: 'rgba(255,255,255,0.82)',
                  border: '1px solid #c4b5fd'
                }}
              >
                <Typography variant="caption" sx={{ color: '#5b21b6', fontWeight: 800 }} display="block">
                  NAMESPACE / DEPLOYMENT
                </Typography>
                <Typography sx={{ fontFamily: 'ui-monospace, Menlo, monospace', fontWeight: 700, fontSize: '0.9rem' }}>
                  {namingSummary.namespace}
                </Typography>
                <Typography variant="caption" sx={{ color: '#5b21b6', fontWeight: 800, mt: 1 }} display="block">
                  DATABASE
                </Typography>
                <Typography sx={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: '0.85rem' }}>
                  {namingSummary.database_name} · user {namingSummary.database_user} · password (random on create)
                </Typography>
                <Typography variant="caption" sx={{ color: '#5b21b6', fontWeight: 800, mt: 1 }} display="block">
                  INFRA BRANCH
                </Typography>
                <Typography sx={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: '0.85rem' }}>
                  {namingSummary.infra_branch_name}
                </Typography>
              </Box>
            )}
            <Tabs
              value={serviceUrlTab}
              onChange={(_, v) => setServiceUrlTab(v)}
              sx={{ mb: 2, bgcolor: 'rgba(255,255,255,0.55)', borderRadius: 1 }}
            >
              <Tab label="Service URLs" />
              <Tab label="K8s Deployments" />
              <Tab label="Pods / Services" />
            </Tabs>

            {!environmentShortNamePreview || !formData.tenant_name || catalogProductsPreview.length === 0 ? (
              <Alert severity="info" sx={{ bgcolor: 'rgba(255,255,255,0.7)' }}>
                Choose a company (tenant name), environment, and at least one Software Application to generate URLs like base-frontend.dev.erp4c.pilvisystems.com.
              </Alert>
            ) : serviceUrlTab === 0 && serviceUrls.length === 0 ? (
              <Alert severity="warning" sx={{ bgcolor: 'rgba(255,255,255,0.7)' }}>
                Select a Software Application with a shortname (e.g. erp4c) to build service URLs.
              </Alert>
            ) : serviceUrlTab === 0 ? (
              <Grid container spacing={2}>
                {serviceUrls.map((row) => (
                  <Grid item xs={12} md={6} key={`${row.software}-${row.role}`}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: 'rgba(255,255,255,0.82)',
                        border: '1px solid #c4b5fd',
                        height: '100%'
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: '#5b21b6', fontWeight: 800, letterSpacing: 0.4 }}
                      >
                        {(row.product_name || row.software).toUpperCase()}
                        {' · '}
                        {row.role.toUpperCase()}
                      </Typography>
                      <Typography
                        sx={{
                          mt: 0.5,
                          fontWeight: 700,
                          wordBreak: 'break-all',
                          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                          fontSize: '0.9rem',
                          color: '#4c1d95'
                        }}
                      >
                        {row.url}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                        host: {row.host}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
                <Grid item xs={12}>
                  <Typography variant="caption" sx={{ color: '#6d28d9' }}>
                    Primary Software Application URLs are saved as Frontend / Backend (instance &amp; API) URLs.
                  </Typography>
                </Grid>
              </Grid>
            ) : (
              <Grid container spacing={2}>
                {namingRows.map((row) => (
                  <Grid item xs={12} md={6} key={`${row.short_name}-${row.role}-${serviceUrlTab}`}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: 'rgba(255,255,255,0.82)',
                        border: '1px solid #c4b5fd',
                        height: '100%'
                      }}
                    >
                      <Typography variant="caption" sx={{ color: '#5b21b6', fontWeight: 800 }}>
                        {(row.product_name || row.short_name).toUpperCase()} · {row.role.toUpperCase()}
                        {row.kind ? ` · ${row.kind}` : ''}
                      </Typography>
                      <Typography
                        sx={{
                          mt: 0.75,
                          fontWeight: 700,
                          wordBreak: 'break-all',
                          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                          fontSize: '0.85rem',
                          color: '#4c1d95'
                        }}
                      >
                        {serviceUrlTab === 1 ? row.deployment_name : row.pod_name_prefix}
                        {serviceUrlTab === 2 ? '<hash>' : ''}
                      </Typography>
                      {serviceUrlTab === 2 ? (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                          Service: <code>{row.service_name}</code>
                        </Typography>
                      ) : null}
                    </Box>
                  </Grid>
                ))}
              </Grid>
            )}

          </SectionCard>

          <SectionCard number={9} title="Kubernetes Infrastructure" theme="teal">
            <Typography variant="body2" sx={{ mb: 2, color: '#0f766e' }}>
              Choose the target cluster for automated provisioning.
            </Typography>
{k8sClusters.length === 0 ? (
              <Alert severity="warning" sx={{ bgcolor: 'rgba(255,255,255,0.75)' }}>
                No enabled Kubernetes clusters found. Add Dev / Prod clusters under Settings → Infrastructure.
              </Alert>
            ) : (
              <FormControl fullWidth required error={Boolean(fieldErrors.k8s_cluster_id)}>
                <InputLabel>Target cluster</InputLabel>
                <Select
                  label="Target cluster"
                  value={k8sClusterId}
                  onChange={(e) => {
                    setK8sClusterId(e.target.value);
                    setFieldErrors((prev) => ({ ...prev, k8s_cluster_id: undefined }));
                  }}
                  sx={{ bgcolor: 'rgba(255,255,255,0.85)' }}
                >
                  {k8sClusters.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.label}
                      {' '}
                      (
                      {c.short_name}
                      {' · '}
                      {c.provider}
                      {' · '}
                      {c.cluster_name}
                      )
                    </MenuItem>
                  ))}
                </Select>
                {fieldErrors.k8s_cluster_id && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                    {fieldErrors.k8s_cluster_id}
                  </Typography>
                )}
              </FormControl>
            )}

          </SectionCard>

          <SectionCard number={10} title="Notes" theme="slate">
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              sx={{ bgcolor: 'rgba(255,255,255,0.9)', borderRadius: 1 }}
            />
          </SectionCard>

          </Grid>

          <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={() => (onCancel ? onCancel() : navigate('/tenants'))}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
              sx={{ background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)' }}
            >
              {saving ? 'Saving...' : (isEdit ? 'Update Tenant' : 'Create Tenant')}
            </Button>
          </Box>
        </form>
      </Paper>

      <ContactFormDialog
        open={contactDialogOpen}
        onClose={() => setContactDialogOpen(false)}
        accountId={formData.account_id}
        lockAccount
        accounts={accounts}
        onSaved={async (contact) => {
          if (!contact?.id) {
            await fetchContacts(formData.account_id);
            return;
          }
          await fetchContacts(formData.account_id);
          setFormData((prev) => ({
            ...prev,
            contact_id: contact.id,
            contact_email: contact.email || prev.contact_email,
            contact_phone: contact.cell_number || prev.contact_phone
          }));
        }}
      />
    </Box>
  );
};

export default TenantForm;
